import { useTranslation } from 'react-i18next';

import { Avatar, Combobox, type ComboboxOption } from '@/shared/ui';

export interface UserOption {
  user_id: string;
  display_name: string;
  /** The unique login (shown after the name when known). */
  login?: string;
}

interface Props {
  label: string;
  users: readonly UserOption[];
  value: string | null;
  onChange: (userId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Users that cannot be picked are left out (e.g. the current owner). */
  exclude?: ReadonlySet<string>;
  id?: string;
}

/** The styled, searchable person picker: avatar, display name and the unique login. */
export function UserSelect({
  label,
  users,
  value,
  onChange,
  placeholder,
  disabled,
  exclude,
  id,
}: Props) {
  const { t } = useTranslation();
  const options: ComboboxOption[] = users
    .filter((user) => !exclude?.has(user.user_id))
    .map((user) => ({
      value: user.user_id,
      label: user.display_name,
      ...(user.login ? { description: `@${user.login}` } : {}),
      leading: <Avatar id={user.user_id} name={user.display_name} size="sm" />,
    }));
  return (
    <Combobox
      label={label}
      options={options}
      value={value}
      searchPlaceholder={t('common.search')}
      emptyText={t('common.noResults')}
      {...(placeholder !== undefined && { placeholder })}
      {...(disabled !== undefined && { disabled })}
      {...(id !== undefined && { id })}
      onChange={onChange}
    />
  );
}
